import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import s from './PersonalCapsuleList.module.css';
import { appAxiosInstance } from "../../api";

interface SubscriptionUser {
    nextWriteoffDate: string;
    cancellationDate: string | null;
    freePeriod: boolean;
    userId: number;
    name: string;
    email: string;
    gender: string;
    requestId: string;
}

interface SubscriptionResponse {
    count: number;
    items: SubscriptionUser[];
}

const PersonalCapsuleList: React.FC = () => {
    const [users, setUsers] = useState<SubscriptionUser[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(10);

    useEffect(() => {
        fetchSubscribers();
    }, [currentPage]);

    const fetchSubscribers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await appAxiosInstance.get<SubscriptionResponse>(
                'backend/getSubscribeUserList',
                {
                    params: {
                        currentPage: currentPage,
                        itemsPerPage: itemsPerPage
                    }
                }
            );
            setUsers(response.data.items);
            setTotalCount(response.data.count);
        } catch (err) {
            setError('Ошибка при загрузке списка пользователей');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const totalPages = Math.ceil(totalCount / itemsPerPage);

    const formatDate = (dateString: string) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getGenderLabel = (gender: string) => {
        const genderMap: Record<string, string> = {
            'male': 'Мужской',
            'female': 'Женский',
            'other': 'Другой'
        };
        return genderMap[gender] || gender || '—';
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    if (error) {
        return <div className={s.error}>{error}</div>;
    }

    return (
        <div className={s.container}>
            <div className={s.header}>
                <h2 className={s.title}>Активные подписки</h2>
                <span className={s.count}>Всего: {totalCount}</span>
            </div>

            <div className={s.tableWrapper}>
                <table className={s.table}>
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>Имя</th>
                        <th>Email</th>
                        <th>Пол</th>
                        <th>Дата списания</th>
                        <th>Статус</th>
                    </tr>
                    </thead>
                    <tbody>
                    {users.length === 0 ? (
                        <tr>
                            <td colSpan={6} className={s.emptyCell}>
                                Нет пользователей с активной подпиской
                            </td>
                        </tr>
                    ) : (
                        users.map((user) => (
                            <tr key={user.userId} className={s.clickableRow}>
                                <Link
                                    to={`/user/${user.userId}?gender=${user.gender}&isSubscriber=true`}
                                    className={s.rowLink}
                                    target="_blank"
                                >
                                    <td>{user.userId}</td>
                                    <td>{user.name || '—'}</td>
                                    <td>{user.email || '—'}</td>
                                    <td>{getGenderLabel(user.gender)}</td>
                                    <td>{formatDate(user.nextWriteoffDate)}</td>
                                    <td>
                                            <span className={user.freePeriod ? s.badgeFree : s.badgePaid}>
                                                {user.freePeriod ? 'Бесплатный период' : 'Активная подписка'}
                                            </span>
                                        {user.cancellationDate && (
                                            <span className={s.badgeCancelled}>
                                                    Отменена
                                                </span>
                                        )}
                                    </td>
                                </Link>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className={s.pagination}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={s.pageButton}
                    >
                        ← Назад
                    </button>
                    <span className={s.pageInfo}>
                        Страница {currentPage} из {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={s.pageButton}
                    >
                        Вперед →
                    </button>
                </div>
            )}
        </div>
    );
};

export default PersonalCapsuleList;